// components/tables/tablesStore.ts
'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { a1ToKey } from './cellRef';
import type { LeasingPropertyDTO, UserContext, AnalystCellStyle as CellStyle, TableSheet, TableDoc } from '#types/types';
export type { AnalystCellStyle as CellStyle, TableSheet, TableDoc } from '#types/types';
import { analystStructuralSheetIds } from '#lib/leasing/analystWorkbook';
import { canEditAnalystWorkbook } from '#lib/access/analystAccess';

/** Cells the agent just wrote/formatted — highlighted transiently in the grid (session-only). */
interface AgentEdit {
  docId: string;
  sheetName: string;
  keys: string[];
  /** optional per-cell rationale ("why") shown on hover */
  whyByKey?: Record<string, string>;
  at: number;
}
/** Result of an ANALYST_READ_RANGE, surfaced to the agent on its next turn then consumed. */
interface LastReadResult {
  sheet: string;
  range: string;
  text: string;
}
/** A cell the grid should scroll to + flash (from a clicked chat citation). */
interface FlashCell {
  sheetName: string;
  key: string;
  nonce: number;
}

interface TablesState {
  sharedScope: string | null;
  sharedLoading: boolean;
  sharedError: string | null;
  shared: Record<string, { revision: number; savedSheets: TableSheet[]; saving: boolean; error: string | null }>;
  resetShared: (scope: string | null) => void;
  hydrateShared: (property: LeasingPropertyDTO, user: UserContext, force?: boolean) => void;
  folder: 'shared' | 'personal';
  setFolder: (folder: 'shared' | 'personal') => void;
  /** all documents by id */
  docs: Record<string, TableDoc>;
  /** doc ids, most-recently-touched first (drives the dashboard order) */
  order: string[];
  /** the open document, or null when showing the dashboard (not persisted) */
  activeDocId: string | null;
  /** bumped on external edits (agent / import / sheet add-remove) to remount the grid */
  rev: number;
  /** sheet name the agent just edited/created — the editor switches to it (consume once) */
  focusSheetName: string | null;
  /** bumped when the agent opens a table, so the Portal brings the Analyst view on-screen */
  viewOpenNonce: number;
  /** id of the sheet currently shown in the editor (what the user is looking at) */
  activeSheetId: string | null;
  /** whether the user is currently viewing the Analyst tool (scopes the agent to it) */
  isAnalystOpen: boolean;
  /** per-document undo/redo snapshots of `sheets` (session-only, not persisted) */
  historyByDoc: Record<string, { undo: TableSheet[][]; redo: TableSheet[][] }>;
  /** most recent ANALYST_READ_RANGE result — surfaced to the agent once then consumed (session) */
  lastRead: LastReadResult | null;
  /** cells the agent just wrote/formatted — transient grid highlight (session) */
  agentEdits: AgentEdit | null;
  /** a cell to scroll-to + flash, set by a clicked chat citation (session) */
  flashCell: FlashCell | null;
  /** timestamp the agent was last active in the Analyst (turn start or a write); drives the
   *  "agent working" glow on the grid. Refreshed continuously during a build, lingers a few
   *  seconds after the last activity, then the grid stops glowing (session, not persisted). */
  agentActiveAt: number | null;
  /** when ON (default), the grid auto-scrolls to follow the cell the agent is writing;
   *  the user can turn it off to navigate freely while the agent works (session-only). */
  autoFollowAgent: boolean;
  /** master-session id → the Analyst doc that master chat is building. The Master builds
   *  models headlessly (no open workspace), so it targets its OWN bound doc instead of the
   *  single global activeDocId — otherwise two master sessions would edit each other's model.
   *  Persisted so a master session resumes its model after reload. */
  masterSessionDocs: Record<string, string>;
  /** a pending "open this doc in the Analyst editor" request raised by a Master
   *  chat's live table card; the Portal consumes it (leave the master session,
   *  open the doc, show the Analyst view). The nonce lets the same doc re-open. */
  openInEditor: { docId: string; sheet?: string; nonce: number } | null;

  // navigation
  openDoc: (id: string) => void;
  closeDoc: () => void;
  /** ask the host (Portal) to navigate to the Analyst view (used after the agent opens a table) */
  requestAnalystView: () => void;
  /** track which sheet the editor is showing, so agent context knows the active sheet */
  setActiveSheetId: (id: string | null) => void;
  setAnalystOpen: (open: boolean) => void;
  /** toggle auto-follow of the agent cursor (session-only) */
  setAutoFollowAgent: (on: boolean) => void;
  /** bind an Analyst doc to a master session (set by ANALYST_CREATE_TABLE/OPEN_TABLE on a master turn) */
  bindMasterSessionDoc: (sessionId: string, docId: string) => void;
  /** a Master chat table card asks the Portal to open this doc in the Analyst editor */
  requestOpenInEditor: (docId: string, sheet?: string) => void;
  consumeOpenInEditor: () => { docId: string; sheet?: string } | null;

  // document CRUD
  createDoc: (name?: string) => string;
  renameDoc: (id: string, name: string) => void;
  deleteDoc: (id: string) => void;
  duplicateDoc: (id: string) => string | null;
  toggleFavorite: (id: string) => void;

  // sheet CRUD
  addSheet: (docId: string, name?: string, rows?: string[][]) => void;
  renameSheet: (docId: string, sheetId: string, name: string) => void;
  removeSheet: (docId: string, sheetId: string) => void;
  /** persist a grid's edits for one sheet (no rev bump — the grid owns its state while typing) */
  setSheetCells: (docId: string, sheetId: string, cells: Record<string, string>) => void;
  /** merge a formatting patch into the given cells of a sheet (presentation only; persisted, no rev bump) */
  setCellStyles: (docId: string, sheetId: string, keys: ReadonlyArray<string>, patch: Partial<CellStyle>) => void;
  /** store a rasterized snapshot for the dashboard card (persisted; no rev / updatedAt bump) */
  setThumbnail: (docId: string, dataUrl: string) => void;
  /** toggle ⋯-menu view/behaviour settings (locked / compact / fullWidth) */
  setDocSetting: (docId: string, patch: Partial<Pick<TableDoc, 'locked' | 'compact' | 'fullWidth' | 'paperView'>>) => void;
  /** undo / redo the active document's sheet edits */
  undo: (docId: string) => void;
  redo: (docId: string) => void;
  /** merge external (agent-authored) cells into a sheet by name, creating it if needed (rev bump) */
  writeSheetCells: (docId: string, sheetName: string, cells: Record<string, string>) => void;
  /** create, overwrite ('replace') or 'append' a whole sheet from a 2D rows array, by name (rev bump) */
  upsertSheet: (docId: string, name: string, rows: string[][], mode?: 'replace' | 'append') => void;
  /** read & clear the sheet name the agent last touched (so the editor switches to it) */
  consumeFocusSheet: () => string | null;
  /** stash an ANALYST_READ_RANGE result for the next agent turn */
  setLastRead: (r: LastReadResult | null) => void;
  /** read & clear the stashed read result (consume-once, mirrors consumeFocusSheet) */
  consumeLastRead: () => LastReadResult | null;
  /** record the cells the agent just changed, for the transient grid highlight */
  setAgentEdits: (e: AgentEdit | null) => void;
  clearAgentEdits: () => void;
  /** request the grid scroll-to + flash a cell (from a clicked chat citation) */
  setFlashCell: (sheetName: string, key: string) => void;
  /** stamp the agent as active right now (drives the grid "working" glow) */
  markAgentActive: () => void;
  /** append imported worksheets as new sheets, with their Excel cell styles (rev bump) */
  importSheets: (
    docId: string,
    sheets: ReadonlyArray<{ name: string; rows: string[][]; styles?: Record<string, CellStyle> }>,
  ) => void;
}

const newId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `id-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;

const now = (): number => Date.now();

export const canEditTable = (doc: TableDoc | undefined): doc is TableDoc => Boolean(doc && (!doc.propertyId || doc.sharedAccess === 'owner'));
export const isStructuralSheet = (doc: TableDoc, sheetId: string): boolean => Boolean(doc.propertyId &&
  analystStructuralSheetIds(doc.propertyId).includes(sheetId));

/** Turn an imported 2-D row array into a sparse "row,col" map (drops blanks). */
function rowsToCells(rows: string[][]): Record<string, string> {
  const cells: Record<string, string> = {};
  rows.forEach((row, r) => {
    row.forEach((v, c) => {
      if (typeof v === 'string' && v !== '') cells[`${r},${c}`] = v;
    });
  });
  return cells;
}

const blankSheet = (name: string): TableSheet => ({ id: newId(), name, cells: {} });

/** Pick a name not already used (case-insensitive) by appending " (n)". */
function uniqueName(base: string, used: ReadonlySet<string>): string {
  const trimmed = base.trim() || 'Sheet';
  if (!used.has(trimmed.toLowerCase())) return trimmed;
  let n = 2;
  while (used.has(`${trimmed} (${n})`.toLowerCase())) n += 1;
  return `${trimmed} (${n})`;
}

// localStorage isn't available during SSR; fall back to a no-op store so the
// module can be imported on the server without throwing.
const ssrSafeStorage = (): Storage => {
  if (typeof window === 'undefined') {
    return { getItem: () => null, setItem: () => undefined, removeItem: () => undefined } as unknown as Storage;
  }
  // Wrap setItem so a quota overflow (snapshots can be large) never crashes persistence.
  return {
    getItem: (k: string) => window.localStorage.getItem(k),
    setItem: (k: string, v: string) => {
      try {
        window.localStorage.setItem(k, v);
      } catch {
        /* quota exceeded — skip persisting this update rather than throw */
      }
    },
    removeItem: (k: string) => window.localStorage.removeItem(k),
  } as Storage;
};

// --- undo / redo history (session-only; recorded by a store subscription) ---
function snapshotSheets(sheets: TableSheet[]): TableSheet[] {
  return sheets.map((sh) => ({ ...sh, cells: { ...sh.cells }, ...(sh.styles ? { styles: { ...sh.styles } } : {}) }));
}
let historyApplying = false;
const lastHistoryAt = new Map<string, number>();
const HISTORY_LIMIT = 60;
const HISTORY_COALESCE_MS = 600;

export const useTablesStore = create<TablesState>()(
  persist(
    (set, get) => ({
      sharedScope: null,
      sharedLoading: false,
      sharedError: null,
      shared: {},
      resetShared: (scope) => {
        historyApplying = true;
        set((s) => ({
          sharedScope: scope, shared: {}, sharedLoading: false, sharedError: null,
          docs: Object.fromEntries(Object.entries(s.docs).filter(([, d]) => !d.propertyId)),
          order: s.order.filter((id) => !s.docs[id]?.propertyId),
          activeDocId: s.activeDocId && s.docs[s.activeDocId]?.propertyId ? null : s.activeDocId,
          historyByDoc: {}, lastRead: null, agentEdits: null, flashCell: null,
          masterSessionDocs: Object.fromEntries(Object.entries(s.masterSessionDocs).filter(([, id]) => !s.docs[id]?.propertyId)),
        }));
        historyApplying = false;
      },
      hydrateShared: (property, user, force = false) => {
        const incoming = property.analystWorkbook;
        if (!incoming) return;
        historyApplying = true;
        set((s) => {
          const current = s.docs[incoming.id];
          const meta = s.shared[incoming.id];
          const dirty = Boolean(current && meta && current.sheets !== meta.savedSheets);
          const sharedAccess = canEditAnalystWorkbook(user, property.leasingManagerIds) ? 'owner' as const : 'reader' as const;
          if (meta && (property.analystRevision ?? 0) < meta.revision) return {};
          if (!force && current && current.sharedAccess === sharedAccess && current.name === property.name && (dirty || meta?.revision === property.analystRevision)) return {};
          if (dirty && !force) return { docs: { ...s.docs, [incoming.id]: { ...current!, sharedAccess } } };
          if (meta && meta.revision === property.analystRevision && !force) return { docs: { ...s.docs, [incoming.id]: { ...current!, sharedAccess } } };
          return {
            docs: { ...s.docs, [incoming.id]: { ...incoming, name: property.name, sharedAccess } },
            shared: { ...s.shared, [incoming.id]: { revision: property.analystRevision ?? 0, savedSheets: incoming.sheets, saving: false, error: null } },
            order: s.order.includes(incoming.id) ? s.order : [...s.order, incoming.id],
            historyByDoc: { ...s.historyByDoc, [incoming.id]: { undo: [], redo: [] } },
            rev: s.rev + 1,
          };
        });
        historyApplying = false;
      },
      folder: 'shared',
      setFolder: (folder) => set({ folder }),
      docs: {},
      order: [],
      activeDocId: null,
      rev: 0,
      focusSheetName: null,
      viewOpenNonce: 0,
      activeSheetId: null,
      isAnalystOpen: false,
      historyByDoc: {},
      lastRead: null,
      agentEdits: null,
      flashCell: null,
      agentActiveAt: null,
      autoFollowAgent: true,
      masterSessionDocs: {},
      openInEditor: null,

      openDoc: (id) => set((s) => {
        const doc = s.docs[id];
        return doc ? { activeDocId: id, folder: doc.propertyId ? 'shared' : 'personal' } : {};
      }),
      closeDoc: () => set({ activeDocId: null }),
      requestAnalystView: () => set((s) => ({ viewOpenNonce: s.viewOpenNonce + 1 })),
      setActiveSheetId: (id) => set((s) => (s.activeSheetId === id ? {} : { activeSheetId: id })),
      setAnalystOpen: (open) => set({ isAnalystOpen: open }),
      setLastRead: (r) => set({ lastRead: r }),
      consumeLastRead: () => {
        const r = get().lastRead;
        if (r) set({ lastRead: null });
        return r;
      },
      setAgentEdits: (e) => set({ agentEdits: e }),
      clearAgentEdits: () => set((s) => (s.agentEdits ? { agentEdits: null } : {})),
      setFlashCell: (sheetName, key) => set((s) => ({ flashCell: { sheetName, key, nonce: (s.flashCell?.nonce ?? 0) + 1 } })),
      markAgentActive: () => set({ agentActiveAt: Date.now() }),
      setAutoFollowAgent: (on) => set((s) => (s.autoFollowAgent === on ? {} : { autoFollowAgent: on })),
      bindMasterSessionDoc: (sessionId, docId) =>
        set((s) => (s.masterSessionDocs[sessionId] === docId ? {} : { masterSessionDocs: { ...s.masterSessionDocs, [sessionId]: docId } })),
      requestOpenInEditor: (docId, sheet) =>
        set((s) => ({ openInEditor: { docId, sheet, nonce: (s.openInEditor?.nonce ?? 0) + 1 } })),
      consumeOpenInEditor: () => {
        const r = get().openInEditor;
        if (r) set({ openInEditor: null });
        return r ? { docId: r.docId, sheet: r.sheet } : null;
      },

      createDoc: (name) => {
        const id = newId();
        const ts = now();
        const doc: TableDoc = {
          id,
          name: (name && name.trim()) || 'Untitled table',
          createdAt: ts,
          updatedAt: ts,
          favorite: false,
          sheets: [blankSheet('Sheet 1')],
        };
        set((s) => ({
          docs: { ...s.docs, [id]: doc },
          order: [id, ...s.order.filter((x) => x !== id)],
          activeDocId: id,
          folder: 'personal',
        }));
        return id;
      },

      renameDoc: (id, name) =>
        set((s) => {
          const d = s.docs[id];
          if (!d || d.propertyId) return {};
          const trimmed = name.trim();
          return { docs: { ...s.docs, [id]: { ...d, name: trimmed || d.name, updatedAt: now() } } };
        }),

      deleteDoc: (id) =>
        set((s) => {
          if (!s.docs[id] || s.docs[id].propertyId) return {};
          const docs = { ...s.docs };
          delete docs[id];
          return {
            docs,
            order: s.order.filter((x) => x !== id),
            activeDocId: s.activeDocId === id ? null : s.activeDocId,
          };
        }),

      duplicateDoc: (id) => {
        const src = get().docs[id];
        if (!src) return null;
        const copyId = newId();
        const ts = now();
        const copy: TableDoc = {
          id: copyId,
          name: `${src.name} copy`,
          createdAt: ts,
          updatedAt: ts,
          sheets: src.sheets.map((sh) => ({
            id: newId(),
            name: sh.name,
            cells: { ...sh.cells },
            ...(sh.styles ? { styles: { ...sh.styles } } : {}),
            ...(sh.paper ? { paper: true } : {}),
          })),
        };
        set((s) => ({
          docs: { ...s.docs, [copyId]: copy },
          order: [copyId, ...s.order.filter((x) => x !== copyId)],
        }));
        return copyId;
      },

      toggleFavorite: (id) =>
        set((s) => {
          const d = s.docs[id];
          if (!d) return {};
          return { docs: { ...s.docs, [id]: { ...d, favorite: !d.favorite } } };
        }),

      addSheet: (docId, name, rows) =>
        set((s) => {
          const d = s.docs[docId];
          if (!canEditTable(d)) return {};
          const used = new Set(d.sheets.map((sh) => sh.name.toLowerCase()));
          const nm = uniqueName(name || `Sheet ${d.sheets.length + 1}`, used);
          const sheet: TableSheet = { id: newId(), name: nm, cells: rows ? rowsToCells(rows) : {} };
          return {
            docs: { ...s.docs, [docId]: { ...d, sheets: [...d.sheets, sheet], updatedAt: now() } },
            rev: s.rev + 1,
            focusSheetName: nm,
          };
        }),

      renameSheet: (docId, sheetId, name) =>
        set((s) => {
          const d = s.docs[docId];
          if (!canEditTable(d) || isStructuralSheet(d, sheetId)) return {};
          const trimmed = name.trim();
          if (!trimmed) return {};
          const used = new Set(d.sheets.filter((sh) => sh.id !== sheetId).map((sh) => sh.name.toLowerCase()));
          const nm = uniqueName(trimmed, used);
          return {
            docs: {
              ...s.docs,
              [docId]: {
                ...d,
                sheets: d.sheets.map((sh) => (sh.id === sheetId ? { ...sh, name: nm } : sh)),
                updatedAt: now(),
              },
            },
            rev: s.rev + 1,
          };
        }),

      removeSheet: (docId, sheetId) =>
        set((s) => {
          const d = s.docs[docId];
          if (!canEditTable(d) || d.sheets.length <= 1 || isStructuralSheet(d, sheetId)) return {};
          return {
            docs: {
              ...s.docs,
              [docId]: { ...d, sheets: d.sheets.filter((sh) => sh.id !== sheetId), updatedAt: now() },
            },
            rev: s.rev + 1,
          };
        }),

      setSheetCells: (docId, sheetId, cells) =>
        set((s) => {
          const d = s.docs[docId];
          if (!canEditTable(d)) return {};
          return {
            docs: {
              ...s.docs,
              [docId]: {
                ...d,
                sheets: d.sheets.map((sh) => (sh.id === sheetId ? { ...sh, cells } : sh)),
                updatedAt: now(),
              },
            },
          };
        }),

      setCellStyles: (docId, sheetId, keys, patch) =>
        set((s) => {
          const d = s.docs[docId];
          if (!canEditTable(d) || !keys.length) return {};
          const sheets = d.sheets.map((sh) => {
            if (sh.id !== sheetId) return sh;
            const styles: Record<string, CellStyle> = { ...(sh.styles ?? {}) };
            for (const k of keys) {
              const cur: Record<string, unknown> = { ...(styles[k] ?? {}) };
              for (const [prop, val] of Object.entries(patch)) {
                if (val === null || val === undefined || val === false || val === '') delete cur[prop];
                else cur[prop] = val;
              }
              if (Object.keys(cur).length === 0) delete styles[k];
              else styles[k] = cur as CellStyle;
            }
            return { ...sh, styles };
          });
          return { docs: { ...s.docs, [docId]: { ...d, sheets, updatedAt: now() } } };
        }),

      setThumbnail: (docId, dataUrl) =>
        set((s) => {
          const d = s.docs[docId];
          if (!d) return {};
          return { docs: { ...s.docs, [docId]: { ...d, thumbnail: dataUrl } } };
        }),

      setDocSetting: (docId, patch) =>
        set((s) => {
          const d = s.docs[docId];
          if (!d) return {};
          return { docs: { ...s.docs, [docId]: { ...d, ...patch } } };
        }),

      undo: (docId) => {
        historyApplying = true;
        set((s) => {
          const h = s.historyByDoc[docId];
          const d = s.docs[docId];
          if (!h || !canEditTable(d) || h.undo.length === 0) return {};
          const undoStack = h.undo.slice();
          const prev = undoStack.pop() as TableSheet[];
          const redoStack = [...h.redo, snapshotSheets(d.sheets)].slice(-HISTORY_LIMIT);
          return {
            docs: { ...s.docs, [docId]: { ...d, sheets: prev, updatedAt: now() } },
            historyByDoc: { ...s.historyByDoc, [docId]: { undo: undoStack, redo: redoStack } },
            rev: s.rev + 1,
          };
        });
        historyApplying = false;
        lastHistoryAt.delete(docId);
      },

      redo: (docId) => {
        historyApplying = true;
        set((s) => {
          const h = s.historyByDoc[docId];
          const d = s.docs[docId];
          if (!h || !canEditTable(d) || h.redo.length === 0) return {};
          const redoStack = h.redo.slice();
          const next = redoStack.pop() as TableSheet[];
          const undoStack = [...h.undo, snapshotSheets(d.sheets)].slice(-HISTORY_LIMIT);
          return {
            docs: { ...s.docs, [docId]: { ...d, sheets: next, updatedAt: now() } },
            historyByDoc: { ...s.historyByDoc, [docId]: { undo: undoStack, redo: redoStack } },
            rev: s.rev + 1,
          };
        });
        historyApplying = false;
        lastHistoryAt.delete(docId);
      },

      writeSheetCells: (docId, sheetName, cells) =>
        set((s) => {
          const d = s.docs[docId];
          if (!canEditTable(d)) return {};
          const lower = sheetName.toLowerCase();
          const exists = d.sheets.some((sh) => sh.name.toLowerCase() === lower);
          const sheets = exists
            ? d.sheets.map((sh) =>
                sh.name.toLowerCase() === lower ? { ...sh, cells: { ...sh.cells, ...cells } } : sh,
              )
            : [...d.sheets, { id: newId(), name: sheetName, cells: { ...cells } }];
          return { docs: { ...s.docs, [docId]: { ...d, sheets, updatedAt: now() } }, rev: s.rev + 1, focusSheetName: sheetName };
        }),

      upsertSheet: (docId, name, rows, mode = 'replace') =>
        set((s) => {
          const d = s.docs[docId];
          if (!canEditTable(d)) return {};
          const lower = name.trim().toLowerCase();
          const existing = d.sheets.find((sh) => sh.name.toLowerCase() === lower);
          if (existing && mode === 'append') {
            let maxRow = -1;
            for (const k of Object.keys(existing.cells)) {
              const r = Number(k.split(',')[0]);
              if (Number.isFinite(r) && r > maxRow) maxRow = r;
            }
            const start = maxRow + 1;
            const cells = { ...existing.cells };
            rows.forEach((row, r) => row.forEach((v, c) => { if (typeof v === 'string' && v !== '') cells[`${start + r},${c}`] = v; }));
            return {
              docs: { ...s.docs, [docId]: { ...d, sheets: d.sheets.map((sh) => (sh.id === existing.id ? { ...sh, cells } : sh)), updatedAt: now() } },
              rev: s.rev + 1,
              focusSheetName: existing.name,
            };
          }
          const cells = rowsToCells(rows);
          if (existing) {
            return {
              docs: { ...s.docs, [docId]: { ...d, sheets: d.sheets.map((sh) => (sh.id === existing.id ? { ...sh, cells } : sh)), updatedAt: now() } },
              rev: s.rev + 1,
              focusSheetName: existing.name,
            };
          }
          const used = new Set(d.sheets.map((sh) => sh.name.toLowerCase()));
          const nm = uniqueName(name, used);
          return {
            docs: { ...s.docs, [docId]: { ...d, sheets: [...d.sheets, { id: newId(), name: nm, cells }], updatedAt: now() } },
            rev: s.rev + 1,
            focusSheetName: nm,
          };
        }),

      consumeFocusSheet: () => {
        const name = get().focusSheetName;
        if (name !== null) set({ focusSheetName: null });
        return name;
      },

      importSheets: (docId, imported) =>
        set((s) => {
          const d = s.docs[docId];
          if (!canEditTable(d)) return {};
          const sheets = d.sheets.slice();
          let firstImported: string | null = null;
          for (const imp of imported) {
            if (!imp.rows.length) continue;
            const used = new Set(sheets.map((sh) => sh.name.toLowerCase()));
            const nm = uniqueName(imp.name || 'Imported', used);
            const sheet: TableSheet = { id: newId(), name: nm, cells: rowsToCells(imp.rows) };
            // xlsx imports carry per-cell Excel styles → render on the light "paper" theme.
            if (imp.styles && Object.keys(imp.styles).length) {
              sheet.styles = imp.styles;
              sheet.paper = true;
            }
            sheets.push(sheet);
            if (!firstImported) firstImported = nm;
          }
          return {
            docs: { ...s.docs, [docId]: { ...d, sheets, updatedAt: now() } },
            rev: s.rev + 1,
            ...(firstImported ? { focusSheetName: firstImported } : {}),
          };
        }),
    }),
    {
      name: 'fater-tables-v1',
      storage: createJSONStorage(ssrSafeStorage),
      // Persist the documents only; the open doc + rev are session/UI state so
      // entering the Tables tool always lands on the dashboard (like Studio).
      partialize: (s) => ({
        docs: Object.fromEntries(Object.entries(s.docs).filter(([, d]) => !d.propertyId)),
        order: s.order.filter((id) => !s.docs[id]?.propertyId),
        masterSessionDocs: Object.fromEntries(Object.entries(s.masterSessionDocs).filter(([, id]) => !s.docs[id]?.propertyId)),
      }),
      merge: (persisted, current) => {
        const saved = persisted as Partial<TablesState>;
        const docs = Object.fromEntries(Object.entries(saved.docs ?? {}).filter(([, d]) => !d.propertyId));
        return { ...current, docs, order: (saved.order ?? []).filter((id) => docs[id]), masterSessionDocs: Object.fromEntries(Object.entries(saved.masterSessionDocs ?? {}).filter(([, id]) => docs[id])) };
      },
      version: 1,
    },
  ),
);

/** True only while the user is viewing the Analyst tool — used to scope the
 *  agent to it (and away from a focused property's Asset Model). */
export const isAnalystViewOpen = (): boolean => useTablesStore.getState().isAnalystOpen;

/**
 * Navigate the Analyst to a cited cell (from a clicked chat citation): find the doc that has the
 * sheet, open it, switch to that sheet, and flash the cell. `sheetName` null => the open doc's
 * active sheet. Brings the Analyst view on-screen.
 */
export function navigateToAnalystCell(sheetName: string | null, a1: string): void {
  const key = a1ToKey(a1);
  if (!key) return;
  const st = useTablesStore.getState();
  let docId = st.activeDocId;
  let sheet = sheetName;
  if (sheetName) {
    const lower = sheetName.toLowerCase();
    const openHas = docId ? Boolean(st.docs[docId]?.sheets.some((s) => s.name.toLowerCase() === lower)) : false;
    if (!openHas) {
      const found = Object.values(st.docs).find((d) => d.sheets.some((s) => s.name.toLowerCase() === lower));
      if (found) docId = found.id;
    }
  } else if (docId) {
    const d = st.docs[docId];
    const cur = d?.sheets.find((s) => s.id === st.activeSheetId) ?? d?.sheets[0];
    sheet = cur?.name ?? null;
  }
  if (!docId || !st.docs[docId]) return;
  st.openDoc(docId);
  if (sheet) useTablesStore.setState({ focusSheetName: sheet });
  st.setFlashCell(sheet ?? '', key);
  st.requestAnalystView();
}

// Record an undo snapshot whenever the active document's sheets change — covers
// user edits, formatting, structural changes and agent edits uniformly. Skips
// undo/redo-driven changes and coalesces rapid edits (typing) into one step.
useTablesStore.subscribe((state, prev) => {
  if (historyApplying) return;
  const id = state.activeDocId;
  if (!id) return;
  const cur = state.docs[id]?.sheets;
  const before = prev.docs[id]?.sheets;
  if (!cur || !before || cur === before) return; // only sheet-content changes
  const t = Date.now();
  const last = lastHistoryAt.get(id) ?? 0;
  lastHistoryAt.set(id, t);
  if (t - last < HISTORY_COALESCE_MS) return; // coalesce the burst into one step
  useTablesStore.setState((s) => {
    const h = s.historyByDoc[id] ?? { undo: [], redo: [] };
    return {
      historyByDoc: {
        ...s.historyByDoc,
        [id]: { undo: [...h.undo, snapshotSheets(before)].slice(-HISTORY_LIMIT), redo: [] },
      },
    };
  });
});
